import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { round, sumBy } from 'lodash';
import { AllocationRule } from 'further-types/lp/allocations';
import { EqualisationFormValues } from '../Editor/EqualiseActions/FormWrapper';
import useAllocationsApi from 'hooks/lp/data/allocations/useAllocationsApi';
import { useLayoutContext } from 'contexts/LayoutContext';
import recalculateLinkedActionAllocations from '../Editor/EqualiseActions/helpers/recalculateLinkedActionAllocations';
import recalculateAndBalanceAllocations from '../Editor/EqualiseActions/helpers/recalculateAndBalanceAllocations';
import { equalisations } from 'further-ui/domain';
import { useEqualisationSection } from '../Editor/EqualisationContext';

type UseEqualiseActionsParams = {
  actionIndex: number;
  linkedActionIndex?: number;
  allocationType: 'originalAllocations' | 'newAllocations';
  inputAmountLcy: number;
};

const useEqualiseActions = ({
  actionIndex,
  linkedActionIndex,
  allocationType,
  inputAmountLcy,
}: UseEqualiseActionsParams) => {
  const { selectedVehicleId } = useLayoutContext();
  const { commitments } = useEqualisationSection();
  const { setValue, watch } = useFormContext<EqualisationFormValues>();
  const {
    generateAllocations,
    initialAllocationsData: newAllocationsData,
    isPending,
  } = useAllocationsApi();

  const isLinkedAction = linkedActionIndex !== undefined;
  const allocationsPath = isLinkedAction
    ? (`actions.${actionIndex}.linkedActions.${linkedActionIndex}.${allocationType}` as const)
    : (`actions.${actionIndex}.${allocationType}` as const);

  const allocationRulePath = isLinkedAction
    ? (`actions.${actionIndex}.linkedActions.${linkedActionIndex}.allocationRule` as const)
    : (`actions.${actionIndex}.allocationRule` as const);

  const watchedFields = watch(allocationsPath);
  const parentActionFields = watch(`actions.${actionIndex}`);

  const setToManualMode = () => {
    setValue(allocationRulePath, AllocationRule.Manual);
  };

  const updateLinkedActionAllocations = (
    parentAllocations: EqualisationFormValues['actions'][number]['newAllocations'],
    index: number,
  ) => {
    const balancedAllocations = recalculateLinkedActionAllocations(
      parentAllocations,
      parentActionFields.linkedActions[index].totalAmountLcy,
      commitments.filter((commitment) =>
        parentAllocations.some(
          (allocation) =>
            allocation.commitmentId === commitment._id &&
            !allocation.excludeFromAllocation,
        ),
      ),
    );

    balancedAllocations.map((newAllocation, newAllocationIndex) => {
      const linkedAllocationPercentagePath =
        `actions.${actionIndex}.linkedActions.${index}.newAllocations.${newAllocationIndex}.allocationPercentage` as const;
      const linkedAllocationAmountLcyPath =
        `actions.${actionIndex}.linkedActions.${index}.newAllocations.${newAllocationIndex}.allocationAmountLcy` as const;
      const linkedAllocationExcludeFromAllocationPath =
        `actions.${actionIndex}.linkedActions.${index}.newAllocations.${newAllocationIndex}.excludeFromAllocation` as const;

      setValue(
        linkedAllocationPercentagePath,
        newAllocation.allocationPercentage,
      );

      setValue(
        linkedAllocationAmountLcyPath,
        newAllocation.allocationAmountLcy,
      );

      setValue(
        linkedAllocationExcludeFromAllocationPath,
        newAllocation.excludeFromAllocation,
      );
    });
  };

  const updateLinkedActionRealisationAllocations = (
    parentAllocations: EqualisationFormValues['actions'][number]['newAllocations'],
    index: number,
  ) => {
    const linkedAction = parentActionFields.linkedActions[index];
    const realisationAllocations = linkedAction.newRealisationAllocations;

    if (!realisationAllocations || realisationAllocations.length === 0) {
      return;
    }

    const totals = linkedAction.newRealisationAllocationTotals;
    if (!totals) {
      return;
    }

    const realisationAllocationsPath =
      `actions.${actionIndex}.linkedActions.${index}.newRealisationAllocations` as const;

    const parentAllocationMap = new Map(
      parentAllocations.map((allocation) => [
        allocation.commitmentId,
        {
          percentage: allocation.allocationPercentage,
          excluded: allocation.excludeFromAllocation,
        },
      ]),
    );

    const totalPercentage = round(
      sumBy(
        parentAllocations.filter((alloc) => !alloc.excludeFromAllocation),
        'allocationPercentage',
      ),
      2,
    );

    let updatedAllocations = realisationAllocations.map((realisationAlloc) => {
      const parentAlloc = parentAllocationMap.get(
        realisationAlloc.commitmentId,
      );

      if (!parentAlloc) return realisationAlloc;

      const newPercentage = parentAlloc.percentage;
      const isExcluded = parentAlloc.excluded;

      if (isExcluded) {
        return {
          ...realisationAlloc,
          allocationPercentage: 0,
          unitsAllocated: 0,
          receivedConsiderationLcy: 0,
          escrowConsiderationLcy: 0,
          earnoutConsiderationLcy: 0,
          withholdingTaxPayableLcy: 0,
          excludeFromAllocation: true,
        };
      }

      return {
        ...realisationAlloc,
        allocationPercentage: newPercentage,
        unitsAllocated: round(totals.totalUnits * (newPercentage / 100), 2),
        receivedConsiderationLcy: round(
          totals.totalReceivedConsiderationLcy * (newPercentage / 100),
          2,
        ),
        escrowConsiderationLcy: round(
          totals.totalEscrowLcy * (newPercentage / 100),
          2,
        ),
        earnoutConsiderationLcy: round(
          totals.totalEarnoutLcy * (newPercentage / 100),
          2,
        ),
        withholdingTaxPayableLcy: !realisationAlloc.withholdingTaxExempt
          ? round(totals.totalWithholdingTaxLcy * (newPercentage / 100), 2)
          : realisationAlloc.withholdingTaxPayableLcy,
        excludeFromAllocation: false,
      };
    });

    if (totalPercentage === 100) {
      updatedAllocations =
        equalisations.recalculateWitholdingTaxForRealisationAllocations(
          updatedAllocations,
          {
            totalWithholdingTaxLcy: totals.totalWithholdingTaxLcy,
          },
        );

      updatedAllocations = equalisations.balanceRealisationAllocations({
        allocations: updatedAllocations,
        totals: {
          totalReceivedConsiderationLcy: totals.totalReceivedConsiderationLcy,
          totalEarnoutLcy: totals.totalEarnoutLcy,
          totalEscrowLcy: totals.totalEscrowLcy,
          totalWithholdingTaxLcy: totals.totalWithholdingTaxLcy,
          totalUnits: totals.totalUnits,
        },
      });
    }

    updatedAllocations.forEach((allocation, idx) => {
      setValue(
        `${realisationAllocationsPath}.${idx}.allocationPercentage`,
        allocation.allocationPercentage,
      );
      setValue(
        `${realisationAllocationsPath}.${idx}.unitsAllocated`,
        allocation.unitsAllocated,
      );
      setValue(
        `${realisationAllocationsPath}.${idx}.receivedConsiderationLcy`,
        allocation.receivedConsiderationLcy,
      );
      setValue(
        `${realisationAllocationsPath}.${idx}.escrowConsiderationLcy`,
        allocation.escrowConsiderationLcy,
      );
      setValue(
        `${realisationAllocationsPath}.${idx}.earnoutConsiderationLcy`,
        allocation.earnoutConsiderationLcy,
      );
      setValue(
        `${realisationAllocationsPath}.${idx}.withholdingTaxPayableLcy`,
        allocation.withholdingTaxPayableLcy,
      );
      setValue(
        `${realisationAllocationsPath}.${idx}.excludeFromAllocation`,
        allocation.excludeFromAllocation,
      );
    });
  };

  const handleRecalculateAndBalanceAllocations = (
    targetAmountLcy: number,
    linkedActionIndex?: number,
  ) => {
    const pathToUse =
      linkedActionIndex !== undefined
        ? (`actions.${actionIndex}.linkedActions.${linkedActionIndex}.newAllocations` as const)
        : allocationsPath;

    const allocations = watch(pathToUse);

    const balancedAllocations = recalculateAndBalanceAllocations({
      allocations: allocations ?? [],
      commitments,
      targetAmountLcy,
    });

    if (balancedAllocations) {
      balancedAllocations.forEach((balancedAllocation, index) => {
        setValue(
          `${pathToUse}.${index}.allocationAmountLcy`,
          balancedAllocation.allocationAmountLcy,
        );
      });
    }
  };

  const setLinkedAllocationValueAndPercentage = ({
    allocationPercentage,
    allocationIndex: allocationIdx,
  }: {
    allocationPercentage: number;
    allocationIndex: number;
  }) => {
    parentActionFields.linkedActions.map((linkedAction, linkedActionIdx) => {
      if (linkedAction.allocationRule !== AllocationRule.LinkedAction) {
        return;
      }
      const linkedAllocationPercentagePath =
        `actions.${actionIndex}.linkedActions.${linkedActionIdx}.newAllocations.${allocationIdx}.allocationPercentage` as const;
      const linkedAllocationAmountLcy =
        `actions.${actionIndex}.linkedActions.${linkedActionIdx}.newAllocations.${allocationIdx}.allocationAmountLcy` as const;

      const newallocationAmountLcy = round(
        (allocationPercentage / 100) * linkedAction.totalAmountLcy,
        2,
      );

      setValue(linkedAllocationAmountLcy, newallocationAmountLcy);
      setValue(linkedAllocationPercentagePath, allocationPercentage);

      handleRecalculateAndBalanceAllocations(
        linkedAction.totalAmountLcy,
        linkedActionIdx,
      );
    });
  };

  const setLinkedAllocationExclusion = (
    excludeFromAllocation: boolean,
    allocationIdx: number,
  ) => {
    parentActionFields.linkedActions.map((linkedAction, linkedActionIdx) => {
      if (
        linkedAction.allocationRule === AllocationRule.PerOriginalInvestment
      ) {
        updateLinkedActionRealisationAllocations(
          parentActionFields.newAllocations,
          linkedActionIdx,
        );
        return;
      }

      if (linkedAction.allocationRule === AllocationRule.LinkedAction) {
        const newAllocationBasePath =
          `actions.${actionIndex}.linkedActions.${linkedActionIdx}.newAllocations.${allocationIdx}` as const;
        setValue(
          `${newAllocationBasePath}.excludeFromAllocation`,
          excludeFromAllocation,
        );
        setValue(`${newAllocationBasePath}.allocationAmountLcy`, 0);
        setValue(`${newAllocationBasePath}.allocationPercentage`, 0);
        return;
      }
    });
  };

  const onChangeAllocationRule = (value: AllocationRule) => {
    setValue(allocationRulePath, value);
    if (value === AllocationRule.LinkedAction && isLinkedAction) {
      updateLinkedActionAllocations(
        parentActionFields.newAllocations,
        linkedActionIndex,
      );
    }

    if (value === AllocationRule.PerOriginalInvestment && isLinkedAction) {
      updateLinkedActionRealisationAllocations(
        parentActionFields.newAllocations,
        linkedActionIndex,
      );
    }

    if (value === AllocationRule.ByCommitmentAndClosingDate) {
      generateAllocations({
        allocationRule: value,
        vehicleId: selectedVehicleId ?? '',
        amountTcy: inputAmountLcy,
        commitmentIdsToConsider:
          watchedFields?.map((item) => item.commitmentId) ?? [],
        excludedCommitmentIds:
          watchedFields
            ?.filter((item) => item.excludeFromAllocation)
            .map((item) => item.commitmentId) ?? [],
        shouldIncludeCommitmentsBeingEqualised: true,
      });
    }
  };

  const handleAllocationAmountChange = (
    newValue: number,
    allocationIdx: number,
  ) => {
    setToManualMode();

    const allocationPercentage = round((newValue / inputAmountLcy) * 100, 5);
    setValue(
      `${allocationsPath}.${allocationIdx}.allocationPercentage`,
      allocationPercentage,
    );

    if (!isLinkedAction) {
      setLinkedAllocationValueAndPercentage({
        allocationPercentage,
        allocationIndex: allocationIdx,
      });
      parentActionFields.linkedActions.forEach(
        (linkedAction, linkedActionIdx) => {
          if (
            linkedAction.allocationRule === AllocationRule.PerOriginalInvestment
          ) {
            updateLinkedActionRealisationAllocations(
              parentActionFields.newAllocations,
              linkedActionIdx,
            );
          }
        },
      );
    }
  };

  const handleAllocationPercentageChange = (
    newValue: number,
    allocationIdx: number,
  ) => {
    setToManualMode();

    const allocationAmountLcy = round((newValue / 100) * inputAmountLcy, 2);
    setValue(
      `${allocationsPath}.${allocationIdx}.allocationAmountLcy`,
      allocationAmountLcy,
    );

    handleRecalculateAndBalanceAllocations(inputAmountLcy);

    if (!isLinkedAction) {
      setLinkedAllocationValueAndPercentage({
        allocationPercentage: newValue,
        allocationIndex: allocationIdx,
      });

      parentActionFields.linkedActions.forEach(
        (linkedAction, linkedActionIdx) => {
          if (
            linkedAction.allocationRule === AllocationRule.PerOriginalInvestment
          ) {
            updateLinkedActionRealisationAllocations(
              parentActionFields.newAllocations,
              linkedActionIdx,
            );
          }
        },
      );
    }
  };

  const handleExcludeFromAllocationChange = (
    newValue: boolean,
    allocationIdx: number,
    allocationRule: AllocationRule,
  ) => {
    if (allocationRule !== AllocationRule.ByCommitmentAndClosingDate) {
      setValue(`${allocationsPath}.${allocationIdx}.allocationAmountLcy`, 0);
      setValue(`${allocationsPath}.${allocationIdx}.allocationPercentage`, 0);
      setValue(
        `${allocationsPath}.${allocationIdx}.excludeFromAllocation`,
        newValue,
      );
      if (!isLinkedAction) {
        setLinkedAllocationExclusion(newValue, allocationIdx);
      } else {
        setToManualMode();
      }
    } else {
      generateAllocations({
        allocationRule: AllocationRule.ByCommitmentAndClosingDate,
        vehicleId: selectedVehicleId ?? '',
        amountTcy: inputAmountLcy,
        commitmentIdsToConsider:
          watchedFields?.map((item) => item.commitmentId) ?? [],
        excludedCommitmentIds:
          watchedFields
            ?.filter((item) => item.excludeFromAllocation)
            .map((item) => item.commitmentId) ?? [],
        shouldIncludeCommitmentsBeingEqualised: true,
      });
    }
  };

  useEffect(() => {
    if (!isPending && newAllocationsData?.allocations) {
      const lcyFormattedAllocationData = newAllocationsData.allocations.map(
        (allocation) => ({
          ...allocation,
          allocationAmountLcy: allocation.allocationAmountTcy,
        }),
      );
      lcyFormattedAllocationData.forEach((allocation, index) => {
        setValue(
          `${allocationsPath}.${index}.allocationPercentage`,
          allocation.allocationPercentage,
        );
        setValue(
          `${allocationsPath}.${index}.allocationAmountLcy`,
          allocation.allocationAmountTcy,
        );
        setValue(
          `${allocationsPath}.${index}.excludeFromAllocation`,
          allocation.excludeFromAllocation,
        );
      });

      parentActionFields.linkedActions.forEach(
        (linkedAction, linkedActionIdx) => {
          if (linkedAction.allocationRule === AllocationRule.LinkedAction) {
            updateLinkedActionAllocations(
              lcyFormattedAllocationData,
              linkedActionIdx,
            );
          }
          if (
            linkedAction.allocationRule === AllocationRule.PerOriginalInvestment
          ) {
            updateLinkedActionRealisationAllocations(
              lcyFormattedAllocationData,
              linkedActionIdx,
            );
          }
        },
      );
    }
  }, [
    newAllocationsData,
    isPending,
    allocationsPath,
    setValue,
    isLinkedAction,
    parentActionFields,
    actionIndex,
  ]);

  const includedAllocations = watchedFields?.filter(
    (item) => !item.excludeFromAllocation,
  );

  const totalAllocationAmountLcy = watchedFields
    ? sumBy(includedAllocations, (item) => Number(item.allocationAmountLcy))
    : 0;

  const totalAllocationPercentage = watchedFields
    ? sumBy(includedAllocations, (item) => Number(item.allocationPercentage))
    : 0;

  const excludedCommitmentNames = watchedFields
    ?.filter((item) => item.excludeFromAllocation)
    .map((item) => item.investorLabel)
    .join(', ');

  return {
    watchedFields,
    isPending,
    allocationsPath,
    totalAllocationAmountLcy,
    totalAllocationPercentage,
    excludedCommitmentNames,
    onChangeAllocationRule,
    handleAllocationAmountChange,
    handleAllocationPercentageChange,
    handleExcludeFromAllocationChange,
    isLinkedAction,
  };
};

export default useEqualiseActions;
